/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Loading from '../components/atoms/loading';
import { ErrorState } from '../components/atoms/states/error-state';
import { useI18n } from '../hooks/provider-bounded/i18n';
import { AuthBootstrapError } from '../errors/auth/auth-bootstrap.error';
import type {
  PermissionsContextType,
  PermissionsProviderProps,
} from '../types/auth/permissions-context.type';
import { getAuthRedirectAppState } from '../utils/auth/auth-recovery.util';
import { useAuthBootstrap } from './auth-bootstrap-context';

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined
);

const getErrorAction = ({
  canRetry,
  handleManualRecovery,
  handleReauthentication,
  requiresManualRecovery,
  requiresReauthentication,
  retry,
  t,
}: {
  canRetry: boolean;
  handleManualRecovery: () => void;
  handleReauthentication: () => void;
  requiresManualRecovery: boolean;
  requiresReauthentication: boolean;
  retry: () => void;
  t: ReturnType<typeof useI18n>['t'];
}) => {
  if (canRetry) {
    return {
      actionContent: t('shared.auth.bootstrap.error.retryAction'),
      onAction: retry,
    };
  }

  if (requiresReauthentication) {
    return {
      actionContent: t('shared.auth.bootstrap.error.reauthAction'),
      onAction: handleReauthentication,
    };
  }

  if (requiresManualRecovery) {
    return {
      actionContent: t('shared.auth.bootstrap.error.manualRecoveryAction'),
      onAction: handleManualRecovery,
    };
  }

  return {
    actionContent: undefined,
    onAction: undefined,
  };
};

/**
 * Provides permission state once auth bootstrap has completed.
 */
export const PermissionsProvider = ({ children }: PermissionsProviderProps) => {
  const { t } = useI18n();
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
  } = useAuth0();
  const { canRetry, error, permissions, loading, ready, retry } =
    useAuthBootstrap();
  const bootstrapError =
    error instanceof AuthBootstrapError ? error : undefined;
  const requiresReauthentication =
    bootstrapError?.kind === 'reauth-required' ||
    bootstrapError?.kind === 'identity-linked';
  const requiresManualRecovery = bootstrapError?.kind === 'manual-recovery';
  const permissionsLoading = auth0Loading || loading;

  const handleReauthentication = () => {
    void loginWithRedirect({
      appState: getAuthRedirectAppState(),
    });
  };

  const handleManualRecovery = () => {
    void loginWithRedirect({
      appState: getAuthRedirectAppState(),
      authorizationParams: { prompt: 'login' },
    });
  };

  const hasPermission = (requiredPermissions: string | string[]): boolean => {
    if (permissionsLoading) {
      return false;
    }

    const requiredPermissionsArray = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions];

    return requiredPermissionsArray.every((permission) =>
      permissions.includes(permission)
    );
  };

  const { actionContent, onAction } = getErrorAction({
    canRetry,
    handleManualRecovery,
    handleReauthentication,
    requiresManualRecovery,
    requiresReauthentication,
    retry,
    t,
  });

  const value: PermissionsContextType = {
    permissions,
    loading: permissionsLoading,
    hasPermission,
  };

  const shouldShowLoading =
    auth0Loading ||
    (isAuthenticated && (loading || (!ready && !bootstrapError)));

  return (
    <PermissionsContext.Provider value={value}>
      {shouldShowLoading ? (
        <div className="flex items-center justify-center h-screen w-full">
          <Loading size="lg" />
        </div>
      ) : isAuthenticated && error ? (
        <div className="flex items-center justify-center h-screen w-full">
          <ErrorState
            className="max-w-md"
            title={t('shared.auth.bootstrap.error.title')}
            description={
              bootstrapError ? t(bootstrapError.messageKey) : undefined
            }
            actionContent={actionContent}
            onAction={onAction}
          />
        </div>
      ) : (
        children
      )}
    </PermissionsContext.Provider>
  );
};

/**
 * Returns the current permission state.
 *
 * @returns Permission context for the authenticated user.
 */
export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);

  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }

  return context;
};
